"use client";

import React, { useEffect, useState, useRef } from "react";
import { usePathname } from "next/navigation";
import Progress from "../atoms/Progress";

const Loader = () => {
  const pathname = usePathname();
  const [progress, setProgress] = useState(0);
  const [hidden, setHidden] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Intercepter les clics sur les liens
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target.closest("a");

      if (link && link.href && link.target !== "_blank") {
        const url = new URL(link.href);
        const currentUrl = new URL(window.location.href);

        // Vérifier si c'est une navigation interne différente
        if (
          url.origin === currentUrl.origin &&
          url.pathname !== currentUrl.pathname
        ) {
          setHidden(false);
          setProgress(0);
          setIsLoading(true);

          // Nettoyage des anciens timers
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
          }
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }

          // Démarrer à 0 puis progresser
          setTimeout(() => {
            setProgress(10);

            // Progression lente et graduelle
            intervalRef.current = setInterval(() => {
              setProgress((prev) => {
                // Progression très lente qui ralentit encore plus vers la fin
                if (prev < 30) {
                  return prev + 3; // Rapide au début
                } else if (prev < 60) {
                  return prev + 1.5; // Moyen
                } else if (prev < 80) {
                  return prev + 0.5; // Lent
                } else if (prev < 90) {
                  return prev + 0.2; // Très lent
                } else {
                  // Stopper à 90% et attendre le changement de pathname
                  if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                  }
                  return 90;
                }
              });
            }, 200);
          }, 50);
        }
      }
    };

    document.addEventListener("click", handleClick, true);

    return () => {
      document.removeEventListener("click", handleClick, true);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // Quand le pathname change, la page est chargée
    if (isLoading) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      // Compléter rapidement jusqu'à 100%
      setProgress(100);
      setIsLoading(false);

      // Masquer après un court délai
      timeoutRef.current = setTimeout(() => {
        setHidden(true);
        setTimeout(() => {
          setProgress(0);
        }, 500);
      }, 500);
    }
  }, [pathname]);

  return (
    !hidden && (
      <Progress
        value={progress}
        className={`${
          hidden ? "opacity-0" : "opacity-100"
        } [&>*]:bg-primaryGreen transition-opacity duration-300 z-[100] fixed top-0 [&>div]:transition-all [&>div]:duration-300 [&>div]:ease-out`}
      />
    )
  );
};

export default Loader;
